import {
  FlyoutListMenuWidget,
  HorizontalOriginOption,
  MenuButtonWidget,
  MenuItemInfo,
  OriginOptions,
  VerticalOriginOption
} from '@tableau/widgets';
import React from 'react';
import { useRef, useState } from 'react';

import { Messages } from '~src/codegen/Localize';
import { IOAuthCredential } from '~src/connectivity/serverApi/types/OAuth/IOAuthCredential';
import {
  integrationSettingConnectButton,
} from '~src/settings/IntegrationsSettings/styles/IntegrationsSettingsStyles';
import {
  SalesforceIntegrationAddCredentialsMenuClickEvent,
  SalesforceIntegrationExistingCredentialsClickEvent
} from '~src/settings/IntegrationsSettings/telemetry/IntegrationsSettingsTelemetry';

export interface ConnectToSalesforceDropdownProps {
  credentials: IOAuthCredential[];
  handleSelectedItem: (credentialId: number) => boolean;
  handleAddNewCredentials: () => void;
  customButtonText?: string;
}

export const ConnectToSalesforce = 'connect-to-salesforce';
export const NewSalesforceConnectionValue = 'newSalesforceConnection';
export const ConnectToSalesforceMenuId = `${ConnectToSalesforce}-menu`;



export function ConnectToSalesforceDropdown(props: ConnectToSalesforceDropdownProps) {
  // Default text for the dropdown button if no custom text is specified
  const defaultText: string = Messages.connectToSalesforce_Title();

  const [buttonText, setButtonText] = useState(defaultText);

  if (props.customButtonText && buttonText !== props.customButtonText) {
    setButtonText(props.customButtonText);
  }

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const connectToSalesforceMenuAnchor = useRef(null);

  const handleButtonClick = (): void => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = (): void => {
    setIsMenuOpen(false);
    connectToSalesforceMenuAnchor.current.focus();
  };

  const handleSelectMenuItem = (oauthId: string): void => {
    if (oauthId === NewSalesforceConnectionValue) {
      SalesforceIntegrationAddCredentialsMenuClickEvent();
      props.handleAddNewCredentials();
    } else {
      executeContextMenuCommand(parseInt(oauthId));
    }
    setIsMenuOpen(false);
  };

  const buildMenuItems = () => {
    let menuItems: MenuItemInfo[] = [];
    if (props.credentials && props.credentials.length > 0) {
      props.credentials.forEach(cred => {
        cred && menuItems.push({ value: cred.id, displayValue: cred.username });
      });
      menuItems.push({ isDivider: true });
    }
    // if for any reason we display the menu without any credentials at least show the connect option
    // the value there is not used, at the end of the flow we will use the id from the response.
    menuItems.push({ value: NewSalesforceConnectionValue, displayValue: Messages.connectToSalesforce_NewSalesforceConnection() });
    return menuItems;
  };

  const executeContextMenuCommand = (oauthId: number): void => {
    if (props.handleSelectedItem(oauthId)) {
      SalesforceIntegrationExistingCredentialsClickEvent();
    }
    if (props.customButtonText) {
      let selectedCredential = props.credentials.find(credential => parseInt(credential.id) === oauthId);
      setButtonText(selectedCredential ? selectedCredential.username : defaultText);
    }
  };

  const bottomLeftOrigin: OriginOptions = {
    vertical: VerticalOriginOption.bottom,
    horizontal: HorizontalOriginOption.left,
  };

  return <>
    <MenuButtonWidget
      aria-controls={ConnectToSalesforceMenuId}
      testId={`${ConnectToSalesforce}-dropdown`}
      kind={'outline'}
      className={integrationSettingConnectButton}
      density={'low'}
      ref={connectToSalesforceMenuAnchor}
      children={buttonText}
      isOpen={isMenuOpen}
      onClick={handleButtonClick}
      aria-label={Messages.connectToSalesforce_Dropdown({CREDENTIAL_NAME: buttonText})}
    />
    {isMenuOpen &&
      <FlyoutListMenuWidget
        id={ConnectToSalesforceMenuId}
        menuItemInfos={buildMenuItems()}
        testId={ConnectToSalesforce} //widget adds suffix
        toggleEl={connectToSalesforceMenuAnchor.current}
        anchorEl={connectToSalesforceMenuAnchor.current}
        anchorOrigin={bottomLeftOrigin}
        onSelect={handleSelectMenuItem}
        requestCloseAll={closeMenu}
      />}
  </>;
}

export default ConnectToSalesforceDropdown;
